import { ModGroupDisplayStyle, ModGroupWalletType, UserRight } from '@biteinc/enums';
import { SyncableMenuModelSchema } from './syncable_menu_model_schema.js';
export const modGroupItemRefSchema = {
    displayName: 'mod group item',
    type: 'object',
    fields: {
        _id: {
            type: 'mongoId',
            isHidden: true,
            isPermanent: true,
            required: true,
        },
        hideFromMenu: {
            type: 'bool',
            displayName: 'Hide from Menu',
            writeRight: UserRight.ManageMenu,
        },
        name: {
            type: 'shortString',
            displayName: 'Custom Name',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        description: {
            type: 'longString',
            displayName: 'Custom Description',
            canHaveLocalizationOverrides: true,
            isNullable: true,
            writeRight: UserRight.ManageMenu,
        },
        selectedByDefaultQuantity: {
            type: 'int',
            displayName: 'Selected by default count',
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'The number of times this mod should be selected by default.',
            },
        },
        initialSelectionQuantity: {
            type: 'int',
            displayName: 'Initial selection quantity',
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'The quantity to set when this mod is first selected by a guest.',
            },
            min: 1,
        },
        maxChoiceQuantity: {
            displayName: 'Max choice quantity',
            type: 'int',
            min: 1,
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'The maximum quantity of this mod that can be ordered as part of this mod group. Vitrine only.',
                isWarning: true,
            },
        },
        mustBeAlwaysSent: {
            type: 'bool',
            displayName: 'Must Always Be Sent to POS',
            writeRight: UserRight.ManageMenu,
        },
        includeInQuickMods: {
            type: 'bool',
            displayName: 'Include in Quick Mods',
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'Add this mod to the mod group’s quick-select preset. When any mods are included, a button above the mod group name selects them all in one tap. Vitrine only.',
            },
        },
        price: {
            type: 'price',
            canBeNegative: true,
            displayName: 'Price Override',
            writeRight: UserRight.ManageMenu,
        },
        autoSelectedSubMods: {
            type: 'array',
            elementType: 'object',
            maxCount: 1,
            displayName: 'Auto-Select Sub Mod',
            placeholder: 'Select a Mod',
            fields: {
                _id: {
                    type: 'mongoId',
                    required: true,
                },
                modGroupId: {
                    type: 'mongoId',
                    required: true,
                },
            },
        },
        substitutionModGroup: {
            type: 'object',
            displayName: 'Substitution',
            writeRight: UserRight.ManageMenu,
            fields: {
                modGroupId: {
                    type: 'mongoId',
                    displayName: 'Mod Group',
                    required: true,
                    ui: 'dropdown',
                },
                priceOverride: {
                    type: 'price',
                    displayName: 'Price Override',
                    required: true,
                },
            },
        },
        colorScheme: {
            type: 'keyString',
            displayName: 'Color Scheme',
            writeRight: UserRight.ManageMenu,
        },
        nutritionInfo: {
            type: 'object',
            displayName: 'Nutrition Info',
            subtype: 'NutritionInfo',
            writeRight: UserRight.ManageMenu,
        },
        images: {
            type: 'array',
            displayName: 'Images',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 5,
            keepAspectRatio: 'contain',
            size: {
                width: 300,
                height: 200,
            },
            jpgSize: {
                width: 600,
                height: 400,
            },
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        recommendationLabel: {
            type: 'shortString',
            displayName: 'Custom Recommendation Label',
            writeRight: UserRight.ManageMenu,
            canHaveLocalizationOverrides: true,
        },
    },
};
function buildParentOrVirtualModGroupFields(includePosVersion) {
    return {
        columnCount: {
            displayName: 'Column Count',
            type: 'int',
            min: 1,
            max: 4,
            defaultValue: null,
            required: true,
            isNullable: true,
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'Integer from 1 to 4. If null, will use the default setting from the location.',
            },
        },
        ...(includePosVersion && {
            posColumnCount: {
                displayName: 'POS Column Count',
                type: 'int',
                min: 1,
                max: 4,
                isPermanent: true,
                isReadOnly: true,
            },
        }),
        prependPlusSignBeforeModPrices: {
            type: 'bool',
            displayName: 'Prepend Plus Sign Before Mod Prices',
            defaultValue: null,
            required: true,
            isNullable: true,
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'If null, will use the default setting from the location.',
            },
        },
        showAsCollapsed: {
            displayName: 'Show in collapsed state in Menu',
            type: 'bool',
            ...(includePosVersion && { isNullable: true }),
            writeRight: UserRight.ManageMenu,
        },
        ...(includePosVersion && {
            posShowAsCollapsed: {
                displayName: 'POS Show in collapsed state in Menu',
                type: 'bool',
                isPermanent: true,
                isReadOnly: true,
            },
        }),
        deemphasize: {
            displayName: 'De-emphasize in Menu',
            type: 'bool',
            ...(includePosVersion && { isNullable: true }),
            tooltip: {
                text: 'Hide this mod group behind a button in the menu interface. minSelectable must be met with selectedByDefaultQuantity and mustBeAlwaysSent modifiers.',
            },
            writeRight: UserRight.ManageMenu,
        },
        ...(includePosVersion && {
            posDeemphasize: {
                displayName: 'POS De-emphasize in Menu',
                type: 'bool',
                isPermanent: true,
                isReadOnly: true,
            },
        }),
        useAsComboBuilder: {
            displayName: 'Use as Combo Builder',
            type: 'bool',
            tooltip: {
                text: 'This group and any others marked with this flag will appear under a special "Make it a combo!" UI. minSelectable has to be 0.',
            },
            writeRight: UserRight.ManageMenu,
        },
        allowMultipleOfSameAddon: {
            displayName: 'Allow selecting more than one of the same mod',
            type: 'bool',
            tooltip: {
                text: 'Changes the min-max rules to allow selecting more than one of the same item. You need to resync after changing this value.',
            },
            writeRight: UserRight.ManageMenu,
        },
        hideFromMenu: {
            displayName: 'Hide from Menu',
            type: 'bool',
            ...(includePosVersion && { isNullable: true }),
            writeRight: UserRight.ManageMenu,
        },
        ...(includePosVersion && {
            posHideFromMenu: {
                displayName: 'POS Hide from Menu',
                type: 'bool',
                isPermanent: true,
                isReadOnly: true,
            },
        }),
    };
}
const subModsSchema = {
    type: 'array',
    maxCount: 1,
    elementType: 'object',
    fields: {
        _id: {
            type: 'mongoId',
            required: true,
        },
        modGroupId: {
            type: 'mongoId',
            required: true,
        },
        i9nId: {
            type: 'keyString',
            required: true,
        },
        modGroupI9nId: {
            type: 'keyString',
            required: true,
        },
    },
};
export const modGroupSchema = {
    displayName: 'modifier group',
    type: 'object',
    fields: {
        ...SyncableMenuModelSchema.fields,
        // Special field only used in menu responses.
        // Indicates that the _id of the model is fake.
        // This originalId is the ID of the real model.
        originalId: {
            type: 'mongoId',
            isHidden: true,
            isReadOnly: true,
        },
        name: {
            type: 'shortString',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        internalName: {
            type: 'shortString',
            displayName: 'Internal Name',
            tooltip: {
                text: 'A name used to identify this modifier group internally. Not seen by guests.',
            },
            writeRight: UserRight.ManageMenu,
        },
        posName: {
            type: 'shortString',
            displayName: 'POS Name',
            isReadOnly: true,
            tooltip: {
                text: 'This is the default display name. The field “Display Name” will replace this name on the kiosk.',
            },
        },
        posInternalName: {
            type: 'keyString',
            isReadOnly: true,
            isHidden: true,
        },
        fulfillmentName: {
            type: 'shortString',
            displayName: 'Fulfillment Name',
            tooltip: {
                text: 'This is the name that will be displayed on the KDS for fulfillment integrations.',
            },
        },
        description: {
            type: 'longString',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        posDescription: {
            type: 'longString',
            displayName: 'POS Description',
            isReadOnly: true,
            tooltip: {
                text: 'This is the default display description. The field “Display Description” will replace this description on the kiosk.',
            },
        },
        vendorId: {
            type: 'mongoId',
            displayName: 'Vendor',
            ui: 'dropdown',
            required: true,
            isPermanent: true,
            writeRight: UserRight.ManageMenu,
        },
        displayStyle: {
            displayName: 'Display Style',
            type: 'stringEnum',
            isNullable: true,
            enum: ModGroupDisplayStyle,
            ui: 'dropdown',
        },
        posDisplayStyle: {
            displayName: 'POS Display Style',
            type: 'stringEnum',
            enum: ModGroupDisplayStyle,
            ui: 'dropdown',
            isPermanent: true,
            isReadOnly: true,
        },
        images: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 5,
            keepAspectRatio: 'fit-width',
            size: {
                // 4:1 ratio
                width: 1080,
                height: 270,
            },
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        hideSelectionCriteria: {
            type: 'bool',
            displayName: 'Hide Mod Group Selection Criteria',
            isNullable: true,
            writeRight: UserRight.ManageMenu,
        },
        posHideSelectionCriteria: {
            type: 'bool',
            displayName: 'POS Hide Mod Group Selection Criteria',
            isPermanent: true,
            isReadOnly: true,
        },
        minSelectable: {
            displayName: 'Minimum number selectable',
            type: 'int',
            min: 0,
            tooltip: {
                text: 'Set to 1 or more if this is mandatory.',
            },
            writeRight: UserRight.ManageMenu,
        },
        maxSelectable: {
            displayName: 'Maximum number selectable',
            type: 'int',
            min: 0,
            tooltip: {
                text: 'You can set both this and Minimum number selectable to 1 to require a single choice.',
            },
            writeRight: UserRight.ManageMenu,
        },
        posMinSelectableOverride: {
            displayName: 'Override POS Min number selectable',
            type: 'int',
            min: 0,
            tooltip: {
                text: 'Must be greater than or equal to the POS-provided min selectable value. No override if 0.',
            },
            writeRight: UserRight.ManageMenu,
        },
        posMaxSelectableOverride: {
            displayName: 'Override POS Max number selectable',
            type: 'int',
            min: 0,
            tooltip: {
                text: 'Must be less than or equal to the POS-provided max selectable value. No override if 0.',
            },
            writeRight: UserRight.ManageMenu,
        },
        isSizeGroup: {
            displayName: 'Is Size Group',
            type: 'bool',
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'Size groups are mod groups where the mods indicate the size of the item.',
            },
        },
        items: {
            displayName: 'Mods',
            type: 'array',
            elementType: 'object',
            maxCount: 5000,
            isHidden: true,
            isPermanent: true,
            fields: modGroupItemRefSchema.fields,
        },
        i9nItems: {
            type: 'array',
            elementType: 'object',
            maxCount: 5000,
            isHidden: true,
            isPermanent: true,
            isReadOnly: true,
            fields: {
                i9nId: {
                    type: 'keyString',
                    required: true,
                },
                hideFromMenu: {
                    type: 'bool',
                },
                selectedByDefaultQuantity: {
                    type: 'int',
                },
                maxChoiceQuantity: {
                    type: 'int',
                },
                price: {
                    type: 'price',
                    canBeNegative: true,
                },
                priceOptionI9nId: {
                    type: 'keyString',
                },
                alwaysDeselect: {
                    type: 'bool',
                },
                dontSendIfSelected: {
                    type: 'bool',
                },
                deselectionSubMods: subModsSchema,
                substitutionSubMods: subModsSchema,
                addSubMods: subModsSchema,
                extraSubMods: subModsSchema,
                modifierCode: {
                    type: 'keyString',
                },
                name: {
                    type: 'keyString',
                },
            },
        },
        ...buildParentOrVirtualModGroupFields(true),
        isComboDecision: {
            displayName: 'Is Combo Decision',
            type: 'bool',
            tooltip: {
                text: 'If true, this mod group will be treated as a combo decision in the POS. This is used to differentiate between regular mod groups and combo builder mod groups in the POS.',
            },
            isNullable: true,
            writeRight: UserRight.ManageMenu,
        },
        posIsComboDecision: {
            displayName: 'POS Is Combo Decision',
            type: 'bool',
            isPermanent: true,
            isReadOnly: true,
        },
        disableAllowMultipleOfSameAddon: {
            displayName: 'Disable multiple of same mod',
            type: 'bool',
            tooltip: {
                text: 'Overrides the above setting for allowing multiple of the same mod.',
            },
            writeRight: UserRight.ManageMenu,
            conditions: [
                {
                    field: 'allowMultipleOfSameAddon',
                    values: [true],
                },
            ],
        },
        hideAggregateSelectionCriteria: {
            type: 'bool',
            displayName: 'Hide Aggregate Quantity Mod Group Selection Criteria',
            isNullable: true,
            writeRight: UserRight.ManageMenu,
        },
        posHideAggregateSelectionCriteria: {
            type: 'bool',
            displayName: 'POS Hide Aggregate Quantity Mod Group Selection Criteria',
            isPermanent: true,
            isReadOnly: true,
        },
        minAggregateQuantity: {
            displayName: 'Minimum aggregate quantity',
            type: 'int',
            min: 0,
            tooltip: {
                text: 'Minimum total quantity of selected mods from this mod group.',
            },
            writeRight: UserRight.ManageMenu,
        },
        maxAggregateQuantity: {
            displayName: 'Maximum aggregate quantity',
            type: 'int',
            min: 0,
            tooltip: {
                text: 'Maximum total quantity of selected mods from this mod group.',
            },
            writeRight: UserRight.ManageMenu,
        },
        posMinAggregateQuantityOverride: {
            displayName: 'Override POS min aggregate quantity',
            type: 'int',
            min: 0,
            tooltip: {
                text: 'Must be greater than or equal to the POS-provided min aggregate quantity. No override if 0.',
            },
            writeRight: UserRight.ManageMenu,
        },
        posMaxAggregateQuantityOverride: {
            displayName: 'Override POS max aggregate quantity',
            type: 'int',
            min: 0,
            tooltip: {
                text: 'Must be less than or equal to the POS-provided max aggregate quantity. No override if 0.',
            },
            writeRight: UserRight.ManageMenu,
        },
        hideChoiceSelectionCriteria: {
            type: 'bool',
            displayName: 'Hide Choice Quantity Mod Group Selection Criteria',
            isNullable: true,
            writeRight: UserRight.ManageMenu,
        },
        posHideChoiceSelectionCriteria: {
            type: 'bool',
            displayName: 'Hide Choice Quantity Mod Group Selection Criteria',
            isPermanent: true,
            isReadOnly: true,
        },
        minChoiceQuantity: {
            displayName: 'Minimum choice quantity',
            type: 'int',
            min: 0,
            tooltip: {
                text: 'Minimum quantity for each mod from this mod group.',
            },
            writeRight: UserRight.ManageMenu,
        },
        maxChoiceQuantity: {
            displayName: 'Maximum choice quantity',
            type: 'int',
            min: 0,
            tooltip: {
                text: 'Maximum quantity for each mod from this mod group.',
            },
            writeRight: UserRight.ManageMenu,
        },
        choiceQuantityIncrement: {
            displayName: 'Choice quantity increment',
            type: 'int',
            min: 0,
            tooltip: {
                text: 'Quantity increment for mods in this mod group. For example, if set to 2, mod quantities within this mod set can only be selected in multiples of 2 (2, 4, 6, etc).',
            },
            writeRight: UserRight.ManageMenu,
        },
        isAssorted: {
            type: 'bool',
            displayName: 'Use as Assorted Mod Group',
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'If true, this mod group will be added to the Assorted options in the item screen.',
            },
        },
        hideWalletSelectionCriteria: {
            type: 'bool',
            displayName: 'Hide Wallet Selection Criteria Text',
            writeRight: UserRight.ManageMenu,
        },
        duplicateModGroup: {
            type: 'bool',
            displayName: 'Create Duplicate Mod Group',
            writeRight: UserRight.ManageMenu,
            tooltip: {
                isWarning: true,
                text: 'Only Brink! The duplicate will not have any price overrides, no POS default selections, etc.',
            },
        },
        posWalletSettings: {
            displayName: 'POS Wallet Settings',
            type: 'object',
            isReadOnly: true,
            fields: {
                type: {
                    displayName: 'Type',
                    type: 'stringEnum',
                    enum: ModGroupWalletType,
                    required: true,
                    isReadOnly: true,
                    tooltip: {
                        text: 'The type of wallet can be quantity (number of choices) or dollar (total cost in cents) value. For quantities, there are tiers of choice caps with associated prices (Up to x choices of mods, charge q per item; the next y choices, charge r per item. and so on).',
                    },
                },
                amount: {
                    displayName: 'Amount',
                    type: 'int',
                    min: 0,
                    isReadOnly: true,
                    tooltip: {
                        text: 'Represented price in cents.',
                    },
                    conditions: [
                        {
                            field: 'type',
                            values: [ModGroupWalletType.Dollar],
                        },
                    ],
                },
                values: {
                    type: 'array',
                    elementType: 'object',
                    ui: 'table',
                    displayName: 'Mod Quantity Caps',
                    isReadOnly: true,
                    conditions: [
                        {
                            field: 'type',
                            values: [ModGroupWalletType.Quantity],
                        },
                    ],
                    fields: {
                        price: {
                            displayName: 'Price per modifier',
                            type: 'int',
                            min: 0,
                            isReadOnly: true,
                        },
                        inclusiveQuantityCap: {
                            displayName: 'Modifier Quantity Cap (Inclusive)',
                            type: 'int',
                            min: 0,
                            isReadOnly: true,
                        },
                    },
                },
            },
        },
        isFloating: {
            type: 'bool',
            isReadOnly: true,
            isHidden: true,
        },
        /**
         * Used by virtual sub mod groups.
         * NOTE: Not stored in the database, but could be stored in cache and needs to be
         * included in this schema to correctly inflate virtual subgroups from cache */
        parentModGroupId: {
            type: 'mongoId',
            isReadOnly: true,
            isHidden: true,
        },
        activeTimings: {
            type: 'array',
            elementType: 'object',
            collapse: true,
            displayName: 'Show Only During',
            placeholder: 'Select a Timing',
            warningHtml: 'This mod group will only be shown on the menu during the times you selected. If nothing is selected, this mod group will always be shown.',
            requiresTimings: true,
            fields: {
                _id: {
                    type: 'mongoId',
                    required: true,
                },
            },
            writeRight: UserRight.ManageMenu,
        },
        virtualSubGroups: {
            displayName: 'Virtual Sub Groups',
            type: 'array',
            elementType: 'object',
            ui: 'accordion',
            uiSubtype: 'VirtualSubGroup',
            fields: {
                _id: {
                    type: 'mongoId',
                    isHidden: true,
                    required: true,
                },
                name: {
                    type: 'shortString',
                    required: true,
                    canHaveLocalizationOverrides: true,
                },
                description: {
                    type: 'shortString',
                    canHaveLocalizationOverrides: true,
                },
                minSelectable: {
                    displayName: 'Minimum number selectable',
                    type: 'int',
                    min: 0,
                    tooltip: {
                        text: "This value should not be smaller the parent's minimum requirement.",
                    },
                },
                maxSelectable: {
                    displayName: 'Maximum number selectable',
                    type: 'int',
                    min: 0,
                    tooltip: {
                        text: "This value should not exceed the parent's maximum requirement.",
                    },
                },
                minAggregateQuantity: {
                    displayName: 'Minimum aggregate quantity',
                    type: 'int',
                    min: 0,
                    tooltip: {
                        text: "This value should not be smaller the parent's minimum requirement.",
                    },
                },
                maxAggregateQuantity: {
                    displayName: 'Maximum aggregate quantity',
                    type: 'int',
                    min: 0,
                    tooltip: {
                        text: "This value should not exceed the parent's maximum requirement.",
                    },
                },
                ...buildParentOrVirtualModGroupFields(false),
                items: {
                    type: 'array',
                    elementType: 'object',
                    required: true,
                    // Disabling the minCount of 1 because items can get removed during the lifetime of the
                    // mod group (e.g. during sync). Otherwise, model inflation will fail.
                    ui: 'table',
                    placeholder: 'Select a Mod',
                    fields: {
                        _id: {
                            type: 'mongoId',
                            required: true,
                        },
                    },
                },
            },
            writeRight: UserRight.ManageMenu,
        },
        variations: {
            type: 'array',
            elementType: 'mongoId',
            isHidden: true,
            isReadOnly: true,
        },
        variationSource: {
            type: 'object',
            isHidden: true,
            isReadOnly: true,
            fields: {
                i9nId: {
                    type: 'keyString',
                    required: true,
                },
                magicCopyKey: {
                    type: 'keyString',
                    required: true,
                },
                i9nIdSuffix: {
                    type: 'keyString',
                    required: true,
                },
                isRoot: {
                    type: 'bool',
                },
            },
        },
    },
    searchFields: [
        'name',
        'posName',
        'internalName',
        'posInternalName',
        'fulfillmentName',
        'posId',
        'i9nId',
    ],
};
//# sourceMappingURL=mod_group_schema.js.map